function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { clamp } from 'lodash-es';
const getCornerPointsForNode = (node, cellWidth, cellHeight, gap)=>{
    const { x, y } = node;
    const left = {
        x: x - cellWidth / 2 - gap,
        y
    };
    const right = {
        x: x + cellWidth / 2 + gap,
        y
    };
    const top = {
        x,
        y: y - cellHeight / 2 - gap
    };
    const bottom = {
        x,
        y: y + cellHeight / 2 + gap
    };
    return {
        left,
        right,
        top,
        bottom
    };
};
// check if the node is colliding with any of the corner points
// if x is greater than left and smaller than right and y is greater than top and smaller than bottom, then it is colliding
const hasCollisionWithAnyPoint = ({ node, cornerPoints, cellWidth, cellHeight, gap })=>{
    const nodeCornerPoint = getCornerPointsForNode(node, cellWidth, cellHeight, gap);
    return cornerPoints.some((corner)=>{
        return corner.x >= nodeCornerPoint.left.x && corner.x <= nodeCornerPoint.right.x && corner.y >= nodeCornerPoint.top.y && corner.y <= nodeCornerPoint.bottom.y;
    });
};
export function getViewportGrids({ points, height, width, leftCornerX, leftCornerY, gap = 60, cellHeight = 80, cellWidth = 80 }) {
    const cellFullHeight = cellHeight + gap;
    const cellFullWidth = cellWidth + gap;
    const noOfRows = Math.round(height / cellFullHeight);
    const noOfColumns = Math.round(width / cellFullWidth);
    const grid = Array.from({
        length: noOfRows
    }, ()=>Array.from({
            length: noOfColumns
        }, ()=>new Set()));
    // mark cell as filled if a node is present in cell
    points.forEach((p)=>{
        const { x, y } = p;
        // no need to consider if the node is not on visible area.
        if (x + cellFullWidth < leftCornerX || // if horizontally its before visible canvas
        y + cellFullHeight < leftCornerY || // if vertically its before visible canvas
        x > leftCornerX + width || // if horizontally its after visible canvas
        y > leftCornerY + height // if vertically its after visible canvas
        ) {
            return;
        }
        const cellX = clamp(Math.round((y - gap / 2 - leftCornerY) / cellFullHeight), 0, noOfRows - 1);
        const cellY = clamp(Math.round((x - gap / 2 - leftCornerX) / cellFullWidth), 0, noOfColumns - 1);
        grid[cellX][cellY].add(p);
    });
    return {
        noOfColumns,
        noOfRows,
        grid,
        cellFullWidth,
        cellFullHeight
    };
}
function getEmptyCellPosition(leftCornerX, leftCornerY, i, j, cellFullWidth, cellFullHeight, offset = {
    x: 0,
    y: 0
}) {
    return {
        cell: {
            x: leftCornerX + j * cellFullWidth,
            y: leftCornerY + i * cellFullHeight
        },
        offset
    };
}
const getAllSurroundingCells = (grid, i, j)=>{
    var _grid_, _grid_i, _grid_i1, _grid_1;
    const topPoints = ((_grid_ = grid[i - 1]) === null || _grid_ === void 0 ? void 0 : _grid_[j]) || new Set();
    const leftPoints = ((_grid_i = grid[i]) === null || _grid_i === void 0 ? void 0 : _grid_i[j - 1]) || new Set();
    const rightPoints = ((_grid_i1 = grid[i]) === null || _grid_i1 === void 0 ? void 0 : _grid_i1[j + 1]) || new Set();
    const bottomPoints = ((_grid_1 = grid[i + 1]) === null || _grid_1 === void 0 ? void 0 : _grid_1[j]) || new Set();
    const allPoints = [
        ...Array.from(topPoints),
        ...Array.from(leftPoints),
        ...Array.from(rightPoints),
        ...Array.from(bottomPoints)
    ];
    return allPoints;
};
export function findNextEmptyCell(params) {
    const { height, width, leftCornerX, leftCornerY, direction = 'horizontal', points, gap = 60 } = params;
    // First try to find empty cell in viewport
    let { grid, noOfRows, noOfColumns, cellFullWidth, cellFullHeight } = getViewportGrids(params);
    const isVertical = direction === 'vertical';
    // avoid adding on top corner, so start with 2nd row
    let startIndex = 1;
    // For vertical layout, scan columns first then rows. For horizontal, scan rows first then columns
    const outerLimit = isVertical ? noOfColumns : noOfRows;
    const innerLimit = isVertical ? noOfRows : noOfColumns;
    for(let outer = startIndex; outer < outerLimit; outer++){
        for(let inner = startIndex; inner < innerLimit; inner++){
            const i = isVertical ? inner : outer;
            const j = isVertical ? outer : inner;
            const cell = grid[i][j];
            if (cell.size === 0) {
                // add the collision detection check here to check collision with the cells in left, right, top and below
                const surroundingCells = getAllSurroundingCells(grid, i, j);
                const emptyCellPosition = getEmptyCellPosition(leftCornerX, leftCornerY, i, j, cellFullWidth, cellFullHeight);
                const collision = hasCollisionWithAnyPoint({
                    node: emptyCellPosition.cell,
                    cornerPoints: surroundingCells,
                    cellWidth: cellFullWidth,
                    cellHeight: cellFullHeight,
                    gap
                });
                if (collision) {
                    continue;
                }
                return emptyCellPosition;
            }
        }
    }
    // Find the rightmost/bottommost node
    const rightmostNode = points.length > 0 ? Math.max(...points.map((p)=>p.x)) : 0;
    const bottommostNode = points.length > 0 ? Math.max(...points.map((p)=>p.y)) : 0;
    // Calculate extended dimensions to include the rightmost/bottommost node plus one cell
    const extendedHeight = isVertical ? Math.max(height, bottommostNode - leftCornerY + cellFullHeight) : height;
    const extendedWidth = isVertical ? width : Math.max(width, rightmostNode - leftCornerX + cellFullWidth);
    const extendedGridResult = getViewportGrids(_object_spread_props(_object_spread({}, params), {
        height: extendedHeight,
        width: extendedWidth
    }));
    const noOfRowsWithExtendedHeight = Math.round(extendedHeight / cellFullHeight);
    const noOfColumnsWithExtendedWidth = Math.round(extendedWidth / cellFullWidth);
    startIndex = 0;
    // For vertical layout, start with startIndex column and scan rows from noOfRows to extended height
    // For horizontal layout, start with startIndex row and scan columns from noOfColumns to extended width
    const startOuter = isVertical ? startIndex : noOfRows;
    const endOuter = isVertical ? noOfColumns : noOfRowsWithExtendedHeight;
    const startInner = isVertical ? noOfRows : noOfColumns;
    const endInner = isVertical ? noOfRowsWithExtendedHeight : noOfColumnsWithExtendedWidth;
    for(let outer = startOuter; outer < endOuter; outer++){
        for(let inner = startInner; inner < endInner; inner++){
            const row = isVertical ? inner : outer;
            const col = isVertical ? outer : inner;
            const cell = extendedGridResult.grid[row][col];
            if (cell.size === 0) {
                const surroundingCells = getAllSurroundingCells(extendedGridResult.grid, row, col);
                const emptyCellPosition = getEmptyCellPosition(leftCornerX, leftCornerY, row, col, cellFullWidth, cellFullHeight);
                const collision = hasCollisionWithAnyPoint({
                    node: emptyCellPosition.cell,
                    cornerPoints: surroundingCells,
                    cellWidth: cellFullWidth,
                    cellHeight: cellFullHeight,
                    gap
                });
                if (collision) {
                    continue;
                }
                // Calculate offset needed to make the cell visible
                const offset = isVertical ? {
                    x: 0,
                    y: -(emptyCellPosition.cell.y - leftCornerY - height)
                } : {
                    x: -(emptyCellPosition.cell.x - leftCornerX - width),
                    y: 0
                };
                return _object_spread_props(_object_spread({}, emptyCellPosition), {
                    offset
                });
            }
        }
    }
    // if we are here that means we couldn't find a empty cell, falling back to position after the last node
    const fallbackCell = isVertical ? {
        cell: {
            x: leftCornerX + cellFullWidth,
            y: bottommostNode + cellFullHeight
        },
        offset: {
            x: 0,
            y: -(bottommostNode + cellFullHeight - leftCornerY - height)
        }
    } : {
        cell: {
            x: rightmostNode + cellFullWidth,
            y: leftCornerY + cellFullHeight
        },
        offset: {
            x: -(rightmostNode + cellFullWidth - leftCornerX - width),
            y: 0
        }
    };
    return fallbackCell;
}
export function getBoundaryFromPoints(points) {
    const xs = points.map((p)=>p.x);
    const ys = points.map((p)=>p.y);
    return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys)
    };
}
